import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import { ATTACHMENT_PARENT_TYPES } from 'features/shared/constants/attachmentParentTypes';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { findAttachments } from 'reducers/helpers/fileHelpers';
import { useAttachmentDialog } from 'features/shared/hooks/useAttachmentDialog';
import { getAttachment, deleteAttachment } from '../../../shared/services/attachmentService';
import { ATTACHMENT_GRID_COLUMNS } from '../../constants/zoneAttachmentsTabConstants';
import FormSpinner from 'components/FormSpinner';

const ZoneAttachmentsTab = ({ record, permissions }) => {
  const zoneId = record.IntCode;
  const dispatch = useDispatch();
  const canEditZone = Boolean(permissions.Edit);
  const { openAttachmentDialog } = useAttachmentDialog();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadAttachments = useCallback(async () => {
    if (!zoneId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => findAttachments(ATTACHMENT_PARENT_TYPES.ZONE, zoneId),
      onSuccess: (data) => setAttachments(data?.data || data || []),
    });
  }, [dispatch, zoneId]);

  const handleAdd = () =>
    openAttachmentDialog({
      parentId: zoneId,
      parentType: ATTACHMENT_PARENT_TYPES.ZONE,
      canSave: canEditZone,
      onSaved: loadAttachments,
    });

  const handleEdit = (attachment) =>
    openAttachmentDialog({
      parentId: zoneId,
      parentType: ATTACHMENT_PARENT_TYPES.ZONE,
      canSave: canEditZone,
      attachment,
      onSaved: loadAttachments,
    });

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.FileName || 'attachment'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteAttachment(row.ptsAttachmentID),
            onSuccess: () => {
              dispatch(notify('Attachment deleted successfully', 'success'));
              loadAttachments();
            },
          });
        },
        'Delete Attachment'
      );
    },
    [dispatch, requestConfirm, loadAttachments]
  );

  const handleDownload = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: async () => {
          const response = await getAttachment(row.ptsAttachmentID);
          const blob = new Blob([response.buffer], { type: response.type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = row.FileName;
          link.click();

          window.URL.revokeObjectURL(url);
        },
      });
    },
    [dispatch]
  );

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  return (
    <Box className="zone-grid-tab">
      {loading && <FormSpinner />}
      <Box className="zone-grid-tab__toolbar">
        <RMSButton
          label="Add Attachment"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditZone}
        />
      </Box>

      <CommonDataGrid
        rows={attachments}
        columns={ATTACHMENT_GRID_COLUMNS}
        getRowId={(row) => row.ptsAttachmentID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Zone Attachments' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditZone },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditZone },
          custom: [
            {
              icon: <GetAppIcon />,
              tooltip: 'Download',
              onClick: handleDownload,
            },
          ],
        }}
      />

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default ZoneAttachmentsTab;
